import { Avatar, Box, Chip, Option, Select, Sheet, Typography } from '@mui/joy';
import { Crown } from '@phosphor-icons/react';
import { useState } from 'react';
import VerifiedBadge from '../../../ui/VerifiedBadge';
import { apiFetch } from '../../../../utils/apiClient';
import { mediaUrl } from '../../../../utils/mediaUrl';

/** Роли: 100 — администратор, всё остальное — обычный участник. */
const ADMIN = 100;
const MEMBER = 1;

const avatarSrc = (avatar) => mediaUrl(avatar) ?? undefined;

/**
 * Состав группы на её странице.
 *
 * Отличается от модалки участников тем, что живёт на странице целиком: список
 * длинный, у каждого — переход в профиль, а администратор может менять роли
 * прямо отсюда, не открывая ничего поверх.
 *
 * @param {Array} members - участники с userId/userType и полями профиля
 * @param {number} currentUserId
 * @param {boolean} isAdmin - может ли текущий пользователь менять роли
 * @param {number|string} chatId
 * @param {Function} onMembersUpdated
 */
export default function GroupMembersList({ members = [], currentUserId, isAdmin = false, chatId, onMembersUpdated = () => { } }) {
    const [saving, setSaving] = useState(null);

    const changeRole = async (userId, userType) => {
        setSaving(userId);
        try {
            const updated = members.map(member => ({
                ...member,
                userType: Number(member.userId) === Number(userId) ? userType : member.userType,
            }));

            const res = await apiFetch(`/api/chats/group/${chatId}/members`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    users: updated.map(m => ({ userId: m.userId, userType: m.userType })),
                }),
            });

            if (!res.ok) throw new Error();

            onMembersUpdated(updated);
        } catch {
            // Роль не сохранилась — список остаётся прежним, повторить можно тем
            // же действием. Отдельного сообщения не показываем: смена роли
            // видна на месте, и её отсутствие само по себе понятно.
        } finally {
            setSaving(null);
        }
    };

    if (members.length === 0) {
        return (
            <Typography level="body-sm" textColor="text.tertiary" sx={{ py: 4, textAlign: 'center' }}>
                Участники не найдены
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            {members.map(member => {
                const isMe = Number(member.userId) === Number(currentUserId);
                const isMemberAdmin = Number(member.userType) === ADMIN;
                const name = [member.firstName, member.lastName].filter(Boolean).join(' ') || 'Пользователь';

                return (
                    <Sheet
                        key={member.userId}
                        variant="plain"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            mb: 0.5,
                            borderRadius: '16px',
                            '&:hover': { backgroundColor: 'neutral.softHoverBg' },
                        }}
                    >
                        <Box
                            component="a"
                            href={`/show/id/${member.userId}`}
                            sx={{ display: 'flex', flexShrink: 0, textDecoration: 'none !important' }}
                        >
                            <Avatar src={avatarSrc(member.avatar)} alt={name} />
                        </Box>

                        <Box
                            component="a"
                            href={`/show/id/${member.userId}`}
                            sx={{ flex: 1, minWidth: 0, textDecoration: 'none !important', color: 'inherit' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                                <Typography level="title-sm" noWrap sx={{ minWidth: 0 }}>
                                    {name}
                                </Typography>
                                {!!member.verified && <VerifiedBadge size={14} type={member.verified} />}
                                {isMe && (
                                    <Chip size="sm" variant="soft" color="neutral" sx={{ ml: 0.5 }}>вы</Chip>
                                )}
                            </Box>
                            {member.username && (
                                <Typography level="body-xs" textColor="text.tertiary" noWrap>
                                    @{member.username}
                                </Typography>
                            )}
                        </Box>

                        {/* Роль администратора видна всем, но менять её может
                            только другой администратор — и не себе: иначе группа
                            рискует остаться без единого управляющего. */}
                        {isAdmin && !isMe ? (
                            <Select
                                size="sm"
                                value={isMemberAdmin ? ADMIN : MEMBER}
                                disabled={saving === member.userId}
                                onChange={(_, value) => value != null && changeRole(member.userId, value)}
                                sx={{ borderRadius: '999px', minWidth: 140 }}
                            >
                                <Option value={MEMBER}>Участник</Option>
                                <Option value={ADMIN}>Администратор</Option>
                            </Select>
                        ) : isMemberAdmin ? (
                            <Chip
                                size="sm"
                                variant="soft"
                                color="primary"
                                startDecorator={<Crown size={14} weight="fill" />}
                            >
                                Админ
                            </Chip>
                        ) : null}
                    </Sheet>
                );
            })}
        </Box>
    );
}
